/**
 * Configuration for documentation sections and pages.
 * This file defines the structure of the documentation system.
 */

// Import markdown files - Introduction & Core Concepts
import whatIsClearpath from "../content/v1/introduction-core-concepts/what-is-clearpath.md?url";
import keyConceptsTerminology from "../content/v1/introduction-core-concepts/key-concepts-terminology.md?url";
import supportedBrowsersDevices from "../content/v1/introduction-core-concepts/supported-browsers-devices.md?url";
import userRolesPermissions from "../content/v1/introduction-core-concepts/user-roles-permissions.md?url";

// Import markdown files - Getting Started
import signingUpLoggingIn from "../content/v1/getting-started/signing-up-logging-in.md?url";
import invitingOnboardingUsers from "../content/v1/getting-started/inviting-onboarding-users.md?url";
import navigatingInterface from "../content/v1/getting-started/navigating-interface.md?url";

// Import markdown files - Role-Based Guides
import guideOrgOwnersAdmins from "../content/v1/role-based-guides/guide-org-owners-admins.md?url";
import guideHrComplianceAdmins from "../content/v1/role-based-guides/guide-hr-compliance-admins.md?url";
import guideManagersSupervisors from "../content/v1/role-based-guides/guide-managers-supervisors.md?url";
import guideEmployees from "../content/v1/role-based-guides/guide-employees.md?url";

// Import markdown files - Organization & User Management
import organizationSetupConfiguration from "../content/v1/organization-user-management/organization-setup-configuration.md?url";
import departmentsEmployeeTypesGroups from "../content/v1/organization-user-management/departments-employee-types-groups.md?url";
import employeesAccessManagement from "../content/v1/organization-user-management/employees-access-management.md?url";
import invitesSelfRegistration from "../content/v1/organization-user-management/invites-self-registration.md?url";

// Import markdown files - Learning & Assessments
import learningPaths from "../content/v1/learning-assessments/learning-paths.md?url";
import assessmentsQuizzes from "../content/v1/learning-assessments/assessments-quizzes.md?url";
import videosMediaContent from "../content/v1/learning-assessments/videos-media-content.md?url";
import certificates from "../content/v1/learning-assessments/certificates.md?url";

// Import markdown files - Policies, Memos, Notes & Legal
import policiesHandbooks from "../content/v1/policies-memos-legal/policies-handbooks.md?url";
import memosInternalCommunications from "../content/v1/policies-memos-legal/memos-internal-communications.md?url";
import notesKnowledgeContent from "../content/v1/policies-memos-legal/notes-knowledge-content.md?url";
import legalDocuments from "../content/v1/policies-memos-legal/legal-documents.md?url";

// Import markdown files - E-Signatures & Documents
import esignaturesOverview from "../content/v1/e-signatures-documents/esignatures-overview.md?url";
import creatingManagingDocuments from "../content/v1/e-signatures-documents/creating-managing-documents.md?url";
import sendingTrackingSignatureRequests from "../content/v1/e-signatures-documents/sending-tracking-signature-requests.md?url";
import managingTemplates from "../content/v1/e-signatures-documents/managing-templates.md?url";
import auditTrailsCompliance from "../content/v1/e-signatures-documents/audit-trails-compliance.md?url";

// Import markdown files - Scheduling, Calendars & Time Off
import calendarsAgendas from "../content/v1/scheduling-calendars-timeoff/calendars-agendas.md?url";
import schedulesShifts from "../content/v1/scheduling-calendars-timeoff/schedules-shifts.md?url";
import timeOffRequests from "../content/v1/scheduling-calendars-timeoff/time-off-requests.md?url";

// Import markdown files - Tasks, Todos & Work Management
import todosTaskLists from "../content/v1/tasks-todos-work/todos-task-lists.md?url";
import kanbanBoards from "../content/v1/tasks-todos-work/kanban-boards.md?url";
import remindersNotifications from "../content/v1/tasks-todos-work/reminders-notifications.md?url";

// Import markdown files - Communication, Discussions & Support
import discussionsThreads from "../content/v1/communication-discussions-support/discussions-threads.md?url";
import commentsOnContent from "../content/v1/communication-discussions-support/comments-on-content.md?url";
import notifications from "../content/v1/communication-discussions-support/notifications.md?url";
import supportHelp from "../content/v1/communication-discussions-support/support-help.md?url";

// Import markdown files - Dashboards, Reports & Analytics
import dashboards from "../content/v1/dashboards-reports-analytics/dashboards.md?url";
import reports from "../content/v1/dashboards-reports-analytics/reports.md?url";
import progressAnalytics from "../content/v1/dashboards-reports-analytics/progress-analytics.md?url";

// Import markdown files - Settings, Security & Compliance
import accountProfileSettings from "../content/v1/settings-security-compliance/account-profile-settings.md?url";
import organizationSettings from "../content/v1/settings-security-compliance/organization-settings.md?url";
import securityAccessControls from "../content/v1/settings-security-compliance/security-access-controls.md?url";
import complianceDataRetention from "../content/v1/settings-security-compliance/compliance-data-retention.md?url";

// Import markdown files - Files, Storage & Integrations
import fileManagement from "../content/v1/files-storage-integrations/file-management.md?url";
import cloudStorageS3 from "../content/v1/files-storage-integrations/cloud-storage-s3.md?url";
import thirdPartyIntegrations from "../content/v1/files-storage-integrations/third-party-integrations.md?url";

// Import markdown files - Billing & Subscription Management
import plansSubscription from "../content/v1/billing-subscription/plans-subscription.md?url";
import billingDetails from "../content/v1/billing-subscription/billing-details.md?url";
import usageLimits from "../content/v1/billing-subscription/usage-limits.md?url";

// Import markdown files - Troubleshooting & FAQs
import commonIssuesSolutions from "../content/v1/troubleshooting-faqs/common-issues-solutions.md?url";
import errorMessagesReference from "../content/v1/troubleshooting-faqs/error-messages-reference.md?url";
import performanceConnectivity from "../content/v1/troubleshooting-faqs/performance-connectivity.md?url";
import contactingSupport from "../content/v1/troubleshooting-faqs/contacting-support.md?url";

export type DocSectionId =
  | "introduction-core-concepts"
  | "getting-started"
  | "role-based-guides"
  | "organization-user-management"
  | "learning-assessments"
  | "policies-memos-legal"
  | "e-signatures-documents"
  | "scheduling-calendars-timeoff"
  | "tasks-todos-work"
  | "communication-discussions-support"
  | "dashboards-reports-analytics"
  | "settings-security-compliance"
  | "files-storage-integrations"
  | "billing-subscription"
  | "troubleshooting-faqs";

export interface DocPageConfig {
  id: string;
  sectionId: DocSectionId;
  slug: string;
  title: string;
  markdownUrl: string;
  order: number;
}

export interface DocSectionConfig {
  id: DocSectionId;
  title: string;
  order: number;
}

export const DOC_SECTIONS: DocSectionConfig[] = [
  {
    id: "introduction-core-concepts",
    title: "Introduction & Core Concepts",
    order: 1,
  },
  { id: "getting-started", title: "Getting Started", order: 2 },
  { id: "role-based-guides", title: "Role-Based Guides", order: 3 },
  {
    id: "organization-user-management",
    title: "Organization & User Management",
    order: 4,
  },
  { id: "learning-assessments", title: "Learning & Assessments", order: 5 },
  {
    id: "policies-memos-legal",
    title: "Policies, Memos, Notes & Legal",
    order: 6,
  },
  { id: "e-signatures-documents", title: "E-Signatures & Documents", order: 7 },
  {
    id: "scheduling-calendars-timeoff",
    title: "Scheduling, Calendars & Time Off",
    order: 8,
  },
  { id: "tasks-todos-work", title: "Tasks, Todos & Work Management", order: 9 },
  {
    id: "communication-discussions-support",
    title: "Communication, Discussions & Support",
    order: 10,
  },
  {
    id: "dashboards-reports-analytics",
    title: "Dashboards, Reports & Analytics",
    order: 11,
  },
  {
    id: "settings-security-compliance",
    title: "Settings, Security & Compliance",
    order: 12,
  },
  {
    id: "files-storage-integrations",
    title: "Files, Storage & Integrations",
    order: 13,
  },
  {
    id: "billing-subscription",
    title: "Billing & Subscription Management",
    order: 14,
  },
  { id: "troubleshooting-faqs", title: "Troubleshooting & FAQs", order: 15 },
];

export const DOC_PAGES: DocPageConfig[] = [
  // Introduction & Core Concepts
  {
    id: "introduction-core-concepts/what-is-clearpath",
    sectionId: "introduction-core-concepts",
    slug: "what-is-clearpath",
    title: "What is ClearPath?",
    markdownUrl: whatIsClearpath,
    order: 1,
  },
  {
    id: "introduction-core-concepts/key-concepts-terminology",
    sectionId: "introduction-core-concepts",
    slug: "key-concepts-terminology",
    title: "Key Concepts & Terminology",
    markdownUrl: keyConceptsTerminology,
    order: 2,
  },
  {
    id: "introduction-core-concepts/supported-browsers-devices",
    sectionId: "introduction-core-concepts",
    slug: "supported-browsers-devices",
    title: "Supported Browsers & Devices",
    markdownUrl: supportedBrowsersDevices,
    order: 3,
  },
  {
    id: "introduction-core-concepts/user-roles-permissions",
    sectionId: "introduction-core-concepts",
    slug: "user-roles-permissions",
    title: "User Roles & Permissions",
    markdownUrl: userRolesPermissions,
    order: 4,
  },
  // Getting Started
  {
    id: "getting-started/signing-up-logging-in",
    sectionId: "getting-started",
    slug: "signing-up-logging-in",
    title: "Signing Up & Logging In",
    markdownUrl: signingUpLoggingIn,
    order: 1,
  },
  {
    id: "getting-started/inviting-onboarding-users",
    sectionId: "getting-started",
    slug: "inviting-onboarding-users",
    title: "Inviting & Onboarding Users",
    markdownUrl: invitingOnboardingUsers,
    order: 2,
  },
  {
    id: "getting-started/navigating-interface",
    sectionId: "getting-started",
    slug: "navigating-interface",
    title: "Navigating the Interface",
    markdownUrl: navigatingInterface,
    order: 3,
  },
  // Role-Based Guides
  {
    id: "role-based-guides/guide-org-owners-admins",
    sectionId: "role-based-guides",
    slug: "guide-org-owners-admins",
    title: "Guide for Organization Owners & Admins",
    markdownUrl: guideOrgOwnersAdmins,
    order: 1,
  },
  {
    id: "role-based-guides/guide-hr-compliance-admins",
    sectionId: "role-based-guides",
    slug: "guide-hr-compliance-admins",
    title: "Guide for HR / Compliance Admins",
    markdownUrl: guideHrComplianceAdmins,
    order: 2,
  },
  {
    id: "role-based-guides/guide-managers-supervisors",
    sectionId: "role-based-guides",
    slug: "guide-managers-supervisors",
    title: "Guide for Managers / Supervisors",
    markdownUrl: guideManagersSupervisors,
    order: 3,
  },
  {
    id: "role-based-guides/guide-employees",
    sectionId: "role-based-guides",
    slug: "guide-employees",
    title: "Guide for Employees",
    markdownUrl: guideEmployees,
    order: 4,
  },
  // Organization & User Management
  {
    id: "organization-user-management/organization-setup-configuration",
    sectionId: "organization-user-management",
    slug: "organization-setup-configuration",
    title: "Organization Setup & Configuration",
    markdownUrl: organizationSetupConfiguration,
    order: 1,
  },
  {
    id: "organization-user-management/departments-employee-types-groups",
    sectionId: "organization-user-management",
    slug: "departments-employee-types-groups",
    title: "Departments, Employee Types & Groups",
    markdownUrl: departmentsEmployeeTypesGroups,
    order: 2,
  },
  {
    id: "organization-user-management/employees-access-management",
    sectionId: "organization-user-management",
    slug: "employees-access-management",
    title: "Employees & Access Management",
    markdownUrl: employeesAccessManagement,
    order: 3,
  },
  {
    id: "organization-user-management/invites-self-registration",
    sectionId: "organization-user-management",
    slug: "invites-self-registration",
    title: "Invites and Self-Registration",
    markdownUrl: invitesSelfRegistration,
    order: 4,
  },
  // Learning & Assessments
  {
    id: "learning-assessments/learning-paths",
    sectionId: "learning-assessments",
    slug: "learning-paths",
    title: "Learning Paths",
    markdownUrl: learningPaths,
    order: 1,
  },
  {
    id: "learning-assessments/assessments-quizzes",
    sectionId: "learning-assessments",
    slug: "assessments-quizzes",
    title: "Assessments & Quizzes",
    markdownUrl: assessmentsQuizzes,
    order: 2,
  },
  {
    id: "learning-assessments/videos-media-content",
    sectionId: "learning-assessments",
    slug: "videos-media-content",
    title: "Videos & Media Content",
    markdownUrl: videosMediaContent,
    order: 3,
  },
  {
    id: "learning-assessments/certificates",
    sectionId: "learning-assessments",
    slug: "certificates",
    title: "Certificates",
    markdownUrl: certificates,
    order: 4,
  },
  // Policies, Memos, Notes & Legal
  {
    id: "policies-memos-legal/policies-handbooks",
    sectionId: "policies-memos-legal",
    slug: "policies-handbooks",
    title: "Policies & Handbooks",
    markdownUrl: policiesHandbooks,
    order: 1,
  },
  {
    id: "policies-memos-legal/memos-internal-communications",
    sectionId: "policies-memos-legal",
    slug: "memos-internal-communications",
    title: "Memos & Internal Communications",
    markdownUrl: memosInternalCommunications,
    order: 2,
  },
  {
    id: "policies-memos-legal/notes-knowledge-content",
    sectionId: "policies-memos-legal",
    slug: "notes-knowledge-content",
    title: "Notes & Knowledge Content",
    markdownUrl: notesKnowledgeContent,
    order: 3,
  },
  {
    id: "policies-memos-legal/legal-documents",
    sectionId: "policies-memos-legal",
    slug: "legal-documents",
    title: "Legal Documents",
    markdownUrl: legalDocuments,
    order: 4,
  },
  // E-Signatures & Documents
  {
    id: "e-signatures-documents/esignatures-overview",
    sectionId: "e-signatures-documents",
    slug: "esignatures-overview",
    title: "E-Signatures Overview",
    markdownUrl: esignaturesOverview,
    order: 1,
  },
  {
    id: "e-signatures-documents/creating-managing-documents",
    sectionId: "e-signatures-documents",
    slug: "creating-managing-documents",
    title: "Creating & Managing Documents",
    markdownUrl: creatingManagingDocuments,
    order: 2,
  },
  {
    id: "e-signatures-documents/sending-tracking-signature-requests",
    sectionId: "e-signatures-documents",
    slug: "sending-tracking-signature-requests",
    title: "Sending & Tracking Signature Requests",
    markdownUrl: sendingTrackingSignatureRequests,
    order: 3,
  },
  {
    id: "e-signatures-documents/managing-templates",
    sectionId: "e-signatures-documents",
    slug: "managing-templates",
    title: "Managing Templates",
    markdownUrl: managingTemplates,
    order: 4,
  },
  {
    id: "e-signatures-documents/audit-trails-compliance",
    sectionId: "e-signatures-documents",
    slug: "audit-trails-compliance",
    title: "Audit Trails & Compliance",
    markdownUrl: auditTrailsCompliance,
    order: 5,
  },
  // Scheduling, Calendars & Time Off
  {
    id: "scheduling-calendars-timeoff/calendars-agendas",
    sectionId: "scheduling-calendars-timeoff",
    slug: "calendars-agendas",
    title: "Calendars & Agendas",
    markdownUrl: calendarsAgendas,
    order: 1,
  },
  {
    id: "scheduling-calendars-timeoff/schedules-shifts",
    sectionId: "scheduling-calendars-timeoff",
    slug: "schedules-shifts",
    title: "Schedules & Shifts",
    markdownUrl: schedulesShifts,
    order: 2,
  },
  {
    id: "scheduling-calendars-timeoff/time-off-requests",
    sectionId: "scheduling-calendars-timeoff",
    slug: "time-off-requests",
    title: "Time Off Requests",
    markdownUrl: timeOffRequests,
    order: 3,
  },
  // Tasks, Todos & Work Management
  {
    id: "tasks-todos-work/todos-task-lists",
    sectionId: "tasks-todos-work",
    slug: "todos-task-lists",
    title: "Todos & Task Lists",
    markdownUrl: todosTaskLists,
    order: 1,
  },
  {
    id: "tasks-todos-work/kanban-boards",
    sectionId: "tasks-todos-work",
    slug: "kanban-boards",
    title: "Kanban Boards",
    markdownUrl: kanbanBoards,
    order: 2,
  },
  {
    id: "tasks-todos-work/reminders-notifications",
    sectionId: "tasks-todos-work",
    slug: "reminders-notifications",
    title: "Reminders & Notifications",
    markdownUrl: remindersNotifications,
    order: 3,
  },
  // Communication, Discussions & Support
  {
    id: "communication-discussions-support/discussions-threads",
    sectionId: "communication-discussions-support",
    slug: "discussions-threads",
    title: "Discussions & Threads",
    markdownUrl: discussionsThreads,
    order: 1,
  },
  {
    id: "communication-discussions-support/comments-on-content",
    sectionId: "communication-discussions-support",
    slug: "comments-on-content",
    title: "Comments on Content",
    markdownUrl: commentsOnContent,
    order: 2,
  },
  {
    id: "communication-discussions-support/notifications",
    sectionId: "communication-discussions-support",
    slug: "notifications",
    title: "Notifications",
    markdownUrl: notifications,
    order: 3,
  },
  {
    id: "communication-discussions-support/support-help",
    sectionId: "communication-discussions-support",
    slug: "support-help",
    title: "Support & Help",
    markdownUrl: supportHelp,
    order: 4,
  },
  // Dashboards, Reports & Analytics
  {
    id: "dashboards-reports-analytics/dashboards",
    sectionId: "dashboards-reports-analytics",
    slug: "dashboards",
    title: "Dashboards",
    markdownUrl: dashboards,
    order: 1,
  },
  {
    id: "dashboards-reports-analytics/reports",
    sectionId: "dashboards-reports-analytics",
    slug: "reports",
    title: "Reports",
    markdownUrl: reports,
    order: 2,
  },
  {
    id: "dashboards-reports-analytics/progress-analytics",
    sectionId: "dashboards-reports-analytics",
    slug: "progress-analytics",
    title: "Progress & Analytics",
    markdownUrl: progressAnalytics,
    order: 3,
  },
  // Settings, Security & Compliance
  {
    id: "settings-security-compliance/account-profile-settings",
    sectionId: "settings-security-compliance",
    slug: "account-profile-settings",
    title: "Account & Profile Settings",
    markdownUrl: accountProfileSettings,
    order: 1,
  },
  {
    id: "settings-security-compliance/organization-settings",
    sectionId: "settings-security-compliance",
    slug: "organization-settings",
    title: "Organization Settings",
    markdownUrl: organizationSettings,
    order: 2,
  },
  {
    id: "settings-security-compliance/security-access-controls",
    sectionId: "settings-security-compliance",
    slug: "security-access-controls",
    title: "Security & Access Controls",
    markdownUrl: securityAccessControls,
    order: 3,
  },
  {
    id: "settings-security-compliance/compliance-data-retention",
    sectionId: "settings-security-compliance",
    slug: "compliance-data-retention",
    title: "Compliance, Data & Retention",
    markdownUrl: complianceDataRetention,
    order: 4,
  },
  // Files, Storage & Integrations
  {
    id: "files-storage-integrations/file-management",
    sectionId: "files-storage-integrations",
    slug: "file-management",
    title: "File Management",
    markdownUrl: fileManagement,
    order: 1,
  },
  {
    id: "files-storage-integrations/cloud-storage-s3",
    sectionId: "files-storage-integrations",
    slug: "cloud-storage-s3",
    title: "Cloud Storage (S3)",
    markdownUrl: cloudStorageS3,
    order: 2,
  },
  {
    id: "files-storage-integrations/third-party-integrations",
    sectionId: "files-storage-integrations",
    slug: "third-party-integrations",
    title: "Third-Party Integrations",
    markdownUrl: thirdPartyIntegrations,
    order: 3,
  },
  // Billing & Subscription Management
  {
    id: "billing-subscription/plans-subscription",
    sectionId: "billing-subscription",
    slug: "plans-subscription",
    title: "Plans & Subscription",
    markdownUrl: plansSubscription,
    order: 1,
  },
  {
    id: "billing-subscription/billing-details",
    sectionId: "billing-subscription",
    slug: "billing-details",
    title: "Billing Details",
    markdownUrl: billingDetails,
    order: 2,
  },
  {
    id: "billing-subscription/usage-limits",
    sectionId: "billing-subscription",
    slug: "usage-limits",
    title: "Usage & Limits",
    markdownUrl: usageLimits,
    order: 3,
  },
  // Troubleshooting & FAQs
  {
    id: "troubleshooting-faqs/common-issues-solutions",
    sectionId: "troubleshooting-faqs",
    slug: "common-issues-solutions",
    title: "Common Issues & Solutions",
    markdownUrl: commonIssuesSolutions,
    order: 1,
  },
  {
    id: "troubleshooting-faqs/error-messages-reference",
    sectionId: "troubleshooting-faqs",
    slug: "error-messages-reference",
    title: "Error Messages Reference",
    markdownUrl: errorMessagesReference,
    order: 2,
  },
  {
    id: "troubleshooting-faqs/performance-connectivity",
    sectionId: "troubleshooting-faqs",
    slug: "performance-connectivity",
    title: "Performance & Connectivity",
    markdownUrl: performanceConnectivity,
    order: 3,
  },
  {
    id: "troubleshooting-faqs/contacting-support",
    sectionId: "troubleshooting-faqs",
    slug: "contacting-support",
    title: "Contacting Support",
    markdownUrl: contactingSupport,
    order: 4,
  },
];

/**
 * Get a doc page configuration by route parameters.
 * @param sectionSlug - The section identifier (e.g., "getting-started")
 * @param pageSlug - The page slug (e.g., "introduction")
 * @returns The doc page configuration or undefined if not found
 */
export function getDocByRoute(
  sectionSlug: string,
  pageSlug: string
): DocPageConfig | undefined {
  return DOC_PAGES.find(
    (page) => page.sectionId === sectionSlug && page.slug === pageSlug
  );
}

/**
 * Get all sections with their associated pages, sorted by order.
 * @returns Array of sections with their pages
 */
export function getSectionsWithPages(): Array<{
  section: DocSectionConfig;
  pages: DocPageConfig[];
}> {
  return DOC_SECTIONS.sort((a, b) => a.order - b.order).map((section) => ({
    section,
    pages: DOC_PAGES.filter((page) => page.sectionId === section.id).sort(
      (a, b) => a.order - b.order
    ),
  }));
}

/**
 * Get the default doc page (first page of first section).
 * @returns The default doc page configuration
 */
export function getDefaultDoc(): DocPageConfig {
  const sections = getSectionsWithPages();
  if (sections.length > 0 && sections[0].pages.length > 0) {
    return sections[0].pages[0];
  }
  return DOC_PAGES[0];
}
